import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Loader2, Star } from "lucide-react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Load Google Maps script
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }

    const existingScript = document.getElementById("google-maps-script");
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(window.google));
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const StopSearch = ({ onPlaceSelect, placeholder = "dive bars near Moab, restaurants in...", initialValue = "", initialLocation = null }) => {
  const mapDivRef = useRef(null);
  const placesServiceRef = useRef(null);
  
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [userLocation, setUserLocation] = useState(initialLocation);
  const [error, setError] = useState(null);

  // Initialize Google Places service
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        await loadGoogleMapsScript();
        
        if (!isMounted) return;

        // Create a hidden map div for PlacesService
        if (!mapDivRef.current) {
          mapDivRef.current = document.createElement('div');
        }
        
        // Create a minimal map instance for PlacesService
        const map = new window.google.maps.Map(mapDivRef.current, {
          center: userLocation || { lat: 38.5733, lng: -109.5498 }, // Default to Moab
          zoom: 10
        });
        
        placesServiceRef.current = new window.google.maps.places.PlacesService(map);
        setIsLoading(false);
      } catch (err) {
        console.error("Failed to load Google Maps:", err);
        setError("Failed to load location search");
        setIsLoading(false);
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, []);

  // Get user location
  useEffect(() => {
    if (!initialLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {
          // Silent fail - use default location
        }
      );
    }
  }, [initialLocation]);

  // Search for places
  const handleSearch = useCallback(() => {
    if (!searchQuery.trim() || !placesServiceRef.current) return;

    setSearching(true);
    setResults([]);
    setSelectedPlace(null);

    // Use textSearch for natural language queries like "dive bars near Moab"
    const request = {
      query: searchQuery,
      location: userLocation || { lat: 38.5733, lng: -109.5498 },
      radius: 50000, // 50km radius
    };

    placesServiceRef.current.textSearch(request, (results, status) => {
      setSearching(false);
      
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
        setResults(results.slice(0, 8)); // Limit to 8 results
      } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        setResults([]);
      }
    });
  }, [searchQuery, userLocation]);

  // Handle search on Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // Handle place selection
  const handleSelectPlace = (place) => {
    setSelectedPlace(place);
    
    const placeData = {
      name: place.name,
      address: place.formatted_address,
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      placeId: place.place_id,
      types: place.types || [],
      rating: place.rating,
      priceLevel: place.price_level,
    };

    setSearchQuery(place.name);
    setResults([]);
    
    if (onPlaceSelect) {
      onPlaceSelect(placeData);
    }
  };

  if (error) {
    return (
      <div className="space-y-2">
        <div className="relative">
          <Input
            type="text"
            placeholder={placeholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-12 bg-[#121212] border-white/10 focus:border-bronze pl-10"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        </div>
        <p className="text-xs text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative">
        <Input
          data-testid="stop-search-input"
          type="text"
          placeholder={isLoading ? "Loading..." : placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          className="h-12 bg-[#121212] border-white/10 focus:border-bronze pl-10 pr-20"
          autoComplete="off"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        
        {/* Search Button */}
        <button
          type="button"
          onClick={handleSearch}
          disabled={isLoading || searching || !searchQuery.trim()}
          className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-bronze hover:bg-bronze-600 text-white text-xs font-oswald uppercase rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : "Search"}
        </button>
      </div>

      {/* Helper Text */}
      {!selectedPlace && results.length === 0 && !searching && (
        <p className="text-xs text-gray-500">
          Try "dive bars near Moab" or "restaurants in Sedona"
        </p>
      )}

      {/* Search Results */}
      {results.length > 0 && (
        <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden max-h-[240px] overflow-y-auto">
          {results.map((place, index) => (
            <div
              key={place.place_id}
              data-testid={`search-result-${index}`}
              className="p-3 cursor-pointer hover:bg-white/5 border-b border-white/5 last:border-0 transition-colors"
              onClick={() => handleSelectPlace(place)}
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-bronze font-oswald font-bold text-sm">{index + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-white truncate">{place.name}</p>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{place.formatted_address}</p>
                  {place.rating && (
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                      <span className="text-xs text-gray-400">{place.rating}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Place Confirmation */}
      {selectedPlace && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-green-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-sm text-white truncate">{selectedPlace.name}</p>
              <p className="text-xs text-gray-400 truncate">{selectedPlace.formatted_address}</p>
            </div>
          </div>
        </div>
      )}

      {/* No Results */}
      {searching && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-bronze" />
          <span className="ml-2 text-sm text-gray-400">Searching...</span>
        </div>
      )}
    </div>
  );
};

export default StopSearch;
